<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />

        <!-- 正常页面 -->
        <div v-else class="recharge-page">
            <!-- 充值金额选择 -->
            <div class="amount-grid">
                <div class="amount-card" v-for="amount in amounts" :key="amount.id"
                    :class="{ selected: selectedAmount && selectedAmount.id === amount.id }"
                    @click="selectAmount(amount)">
                    <span class="amount-text">¥{{ amount.recharge_amount }}</span>
                    <van-icon v-if="selectedAmount && selectedAmount.id === amount.id" name="success"
                        class="check-icon" />
                </div>
            </div>

            <!-- 充值赠送 -->
            <div class="gift-section" v-if="rechargeGifts">
                <div class="gift-title">充值赠送</div>
                <div class="gift-list">
                    <div class="gift-item">
                        <div class="gift-icon">
                            <van-icon name="card" color="#8B0000" size="20" />
                        </div>
                        <div class="gift-content">
                            <div class="gift-name">
                                <span>{{ rechargeGifts.card_name }}</span>
                                <span class="gift-tag" :class="rechargeGifts.card_type === '金额卡' ? 'amount-tag' : 'quantity-tag'">
                                    {{ rechargeGifts.card_type}}
                                </span>
                            </div>
                            <div class="gift-desc">充值成功后发放到个人帐户，在卡片中查看</div>
                        </div>
                        <div class="gift-value">
                            <span v-if="rechargeGifts.card_type === '金额卡'">¥{{ rechargeGifts.card_money }}</span>
                            <span v-else>{{ rechargeGifts.bottle_num }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 充值须知 -->
            <div class="notice-section">
                <div class="notice-title">充值须知</div>
                <div class="notice-list">
                    <div class="notice-item" v-for="item in recharge_notice.split('\n')" :key="item">{{ item }}</div>
                </div>
            </div>

            <!-- 充值按钮 -->
            <div class="recharge-action">
                <van-button class="recharge-btn" type="primary" @click="handleRecharge" :disabled="!selectedAmount"
                    :loading="submitting">
                    {{ submitting ? '充值中...' : '立即充值' }}
                </van-button>
            </div>
        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { setWxTitle } from '@/utils/wx'
import { post } from '@/utils/request'
import { WECHAT_APPID } from '../../../config/env'
import { Dialog, Toast } from 'vant'

// 加载状态
const loading = ref(true)
const submitting = ref(false)

// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 充值金额选项
const amounts = ref([])
// 充值说明
const recharge_notice = ref('')

// 充值赠送
const rechargeGifts = ref(false)

// 选中的金额
const selectedAmount = ref(null)

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

onMounted(() => {
    setWxTitle('充值')
    initPage()
})

// 页面初始化
const initPage = async () => {
    try {
        // 这里可以添加页面初始化逻辑，比如获取用户信息等
        const res = await post('/api/select/community/recharge/amount', {
            app_id: WECHAT_APPID,
        })
        if (res.result && res.result.state === 'ok') {
            amounts.value = res.result.recharge_amount_arr
            if (amounts.value.length > 0) {
                selectedAmount.value = amounts.value[0]
                rechargeGifts.value = amounts.value[0].give_card_info
            }
            recharge_notice.value = res.result.recharge_description
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }
    } catch (error) {
        console.error('页面初始化失败:', error)
        handleError('加载失败', '页面加载失败，请重试')
    } finally {
        loading.value = false
    }
}

// 选择金额
const selectAmount = (amount) => {
    selectedAmount.value = amount
    rechargeGifts.value = amount.give_card_info
}

// 充值处理
const handleRecharge = async () => {
    if (!selectedAmount.value) return

    // 这里可以调用支付接口
    console.log('充值金额:', selectedAmount.value)
    // TODO: 实现充值逻辑
    try {
        submitting.value = true
        const res = await post('/api/create/community/wechat/order', {
            type: '充值',
            partner_id: parseInt(localStorage.getItem('userId')),
            app_id: WECHAT_APPID,
            order_money: selectedAmount.value.recharge_amount,
            give_card_id: rechargeGifts.value.id,
            // order_money: 0.01
        })

        if (res.result.state === 'ok') {
            if (selectedAmount.value.recharge_amount > 0) {
                // 调用微信支付
                WeixinJSBridge.invoke('getBrandWCPayRequest', {
                    "appId": res.result.appId,     //公众号ID，由商户传入     
                    "timeStamp": res.result.timeStamp,     //时间戳，自1970年以来的秒数     
                    "nonceStr": res.result.nonceStr,      //随机串     
                    "package": res.result.package,
                    "signType": res.result.signType,     //微信签名方式：     
                    "paySign": res.result.paySign //微信签名 
                },
                    function (res) {
                        if (res.err_msg == "get_brand_wcpay_request:ok") {
                            // 使用以上方式判断前端返回,微信团队郑重提示：
                            //res.err_msg将在用户支付成功后返回ok，但并不保证它绝对可靠，商户需进一步调用后端查单确认支付结果。
                            window.history.back()
                        }
                    });
            } else {
                window.history.back()
            }
        } else {
            Dialog.alert({
                title: '提示',
                message: res.result.message
            })
        }
    } catch (error) {
        console.error('充值失败:', error)
        Toast.fail('充值失败，请重试')
    } finally {
        submitting.value = false
    }
}
</script>

<style scoped>
.recharge-page {
    min-height: 100vh;
    background: #fff;
    padding: 0 14px;
    display: flex;
    flex-direction: column;
}

.amount-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin: 28px 0 22px 0;
}

.amount-card {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #979797;
    border-radius: 5px;
    width: 98px;
    height: 76px;
    transition: all 0.2s ease;
}

.amount-card.selected {
    border-color: var(--van-primary-color);
    background: #fef2f2;
}

.amount-text {
    font-size: 24px;
    font-weight: 600;
    color: #2C261F;
}

.check-icon {
    position: absolute;
    top: -1px;
    right: -0;
    background: var(--van-primary-color);
    font-size: 16px;
    font-weight: 500;
    color: #fff;
    border-radius: 0px 5px;
    width: 21px;
    height: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.gift-section {
    margin-bottom: 22px;
}

.gift-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 14px;
}

.gift-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.gift-item {
    display: flex;
    align-items: center;
    padding: 11px;
    background: #FEF2F2;
    border-radius: 7px;
    position: relative;
}

.gift-icon {
    margin-right: 12px;
    margin-top: 2px;
}

.gift-content {
    flex: 1;
    min-width: 0;
}

.gift-name {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    margin-bottom: 6px;
}

.gift-tag {
    display: inline-block;
    font-size: 10px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 10px;
    margin-left: 6px;
}

.gift-tag.amount-tag {
    background-color: #C5E2FA;
    color: #10A7FF;
}

.gift-tag.quantity-tag {
    background-color: #C1E5B8;
    color: #1CD150;
}

.gift-desc {
    font-size: 12px;
    color: #6B7280;
    line-height: 1.5;
}

.gift-value {
    font-size: 14px;
    font-weight: 500;
    color: var(--van-primary-color);
    white-space: nowrap;
}

.notice-section {
    flex: 1;
    margin-bottom: 32px;
}

.notice-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 10px;
}

.notice-list {
    display: flex;
    flex-direction: column;
}

.notice-item {
    font-size: 12px;
    color: #3C3C3C;
    line-height: 1.8;
}

.recharge-action {
    padding-bottom: calc(env(safe-area-inset-bottom) + 20px);
}

.recharge-btn {
    width: 100%;
    height: 40px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
}
</style>